import React, { useEffect, useRef, useState } from 'react';

import {
  DisplayPlacementId,
  PlacementStatus
} from 'node_modules/@schibsted-nmp/advertising-shared/src/types/api/index.js';
import type { LayoutType } from 'node_modules/@schibsted-nmp/advertising-shared/src/types/layouts/search.js';

import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { $retailMediaAtom } from '@client/core/atoms/retailMedia.js';
import { useInScreen } from '@client/core/hooks/index.js';
import { useStore } from '@nanostores/react';
import {
  BapSearchAd,
  AdImage
} from '@schibsted-nmp/search-frontend-components/react';

import { DsaModal } from '../DsaModal.js';
import type { RetailMediaProduct } from '../types/index.js';
import { trackClick, trackImpression } from '../utils/RetailMediaApi.js';

export type SimpleBapSearchAdProps = {
  layout?: LayoutType;
  onClick?: () => void;
  products: Array<RetailMediaProduct>;
};

export type LabelRenderProps = {
  className?: string;
  classes: {
    badge: string;
    info: string;
    warning: string;
    success: string;
    [key: string]: string;
  };
};

function optimizeImageUrl(url: string): string {
  const params = new URLSearchParams({
    w: '320',
    q: '30',
    f: 'webp'
  });
  return `${url}?${params.toString()}`;
}

function convertToBapFormat(product: RetailMediaProduct) {
  return {
    ad_id: product.adId,
    canonical_url: product.link,
    distance: null,
    extras: [],
    flags: [],
    heading: product.title,
    image: {
      aspect_ratio: 1.3333333333333333,
      height: 400,
      url: optimizeImageUrl(product.imageLink),
      width: 533
    },
    labels: [],
    location: '',
    main_search_key: 'SEARCH_ID_BAP_COMMON',
    organisation_name: product.organisationName,
    price: {
      currency_code: 'NOK',
      amount: product.price,
      price_unit: 'kr'
    },
    timestamp: undefined,
    trade_type: 1
  };
}

export function SponsoredProductAd({
  layout: initialLayout,
  onClick,
  products
}: SimpleBapSearchAdProps) {
  const product = products[0];
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Subscribe to atom for layout changes to re-render component as grid/list
  const retailMediaState = useStore($retailMediaAtom);
  const layout = retailMediaState.layoutType ?? initialLayout ?? 'grid';

  const adRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);

  // Track viewability
  const { hasIntersected } = useInScreen({
    ref: adRef,
    threshold: 0.5,
    rootMargin: '0px'
  });

  useEffect(() => {
    if (hasIntersected && !hasTrackedView.current && product) {
      hasTrackedView.current = true;
      updateMetricByKey(
        DisplayPlacementId.AdvtSplMiddle1,
        PlacementStatus.AdViewed
      );

      void trackImpression({
        adId: product.adId,
        itemId: product.itemId,
        placement: 'home',
        categories: product.categories,
        store: 'finn_recommerce'
      });
    }
  }, [hasIntersected, product]);

  const handleClick = () => {
    if (product) {
      void trackClick({
        adId: product.adId,
        itemId: product.itemId,
        placement: 'home',
        categories: product.categories,
        store: 'finn_recommerce'
      });
    }

    onClick?.();
  };

  if (!product) return null;

  const bapAd = convertToBapFormat(product);

  const IMAGE_SIZES =
    '(min-width: 1300px) 306px, (min-width: 1010px) 316px, (min-width: 768px) 33vw, (min-width: 480px) 50vw, 100vw';

  return (
    <>
      <div ref={adRef}>
        <BapSearchAd
          ad={bapAd}
          id={bapAd.ad_id}
          imageSizes={IMAGE_SIZES}
          layout={layout}
          onClick={handleClick}
          url={bapAd.canonical_url}
          target="_blank"
          className="sponsored-product-ad"
          label={
            layout === 'list'
              ? () => (
                  <div
                    className="flex justify-between items-center text-sm advertisement-label"
                    style={{
                      position: 'absolute',
                      top: '8px',
                      left: 0,
                      right: 0,
                      paddingRight: '1rem',
                      zIndex: 10,
                      pointerEvents: 'none'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1B1B1F'
                      }}
                    >
                      Reklame
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsModalOpen(true);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#0063FB',
                        fontWeight: 300,
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '14px',
                        pointerEvents: 'auto'
                      }}
                    >
                      Ad info
                    </button>
                  </div>
                )
              : undefined
          }
          image={
            layout === 'grid'
              ? () => (
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        zIndex: 1,
                        pointerEvents: 'none'
                      }}
                    >
                      <span
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#1B1B1F'
                        }}
                      >
                        Reklame
                      </span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsModalOpen(true);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0063FB',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: 300,
                          cursor: 'pointer',
                          padding: 0,
                          position: 'relative',
                          zIndex: 2,
                          pointerEvents: 'auto'
                        }}
                      >
                        Ad info
                      </button>
                    </div>
                    <AdImage
                      aspectClass="aspect-3/4"
                      imageClass="s-bg"
                      height={bapAd.image.height}
                      width={bapAd.image.width}
                      url={bapAd.image.url}
                      crop={false}
                      sizes={IMAGE_SIZES}
                    />
                  </div>
                )
              : undefined
          }
        />
      </div>

      <DsaModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        product={product}
      />
    </>
  );
}
